import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { Box } from '@mui/material';
import { FitnessCenter } from '@mui/icons-material';

// Cadencia del ciclo y duración del fundido. El fundido dura ~la mitad del ciclo para que
// los dos fotogramas se solapen a mitad de camino (crossfade real, no parpadeo).
const CYCLE_MS = 900;
const FADE = 'opacity 420ms cubic-bezier(0.32, 0.72, 0, 1)';

/**
 * OTRIX Play — stop-motion image: alterna dos fotogramas para simular la animación del
 * ejercicio. Precarga ambos frames (decode) antes de arrancar el ciclo para evitar el
 * flash del primer intercambio en frío. Fallback a frame único o placeholder.
 *
 * `radius`/`bgcolor` existen para cuando el recuadro contenedor ya pone su propio borde,
 * radio y fondo (la tira de fotogramas del admin): ahí se pasan a 0/transparent y la celda
 * de la vista previa queda idéntica a las de los fotogramas, sin doble bisel ni doble fondo.
 */
function StopMotionImage({ frame1 = null, frame2 = null, alt = 'Ejercicio', height = 180, playing = true, radius = 2, bgcolor = 'surface.raised' }) {
  const [showSecond, setShowSecond] = useState(false);
  const [ready, setReady] = useState(false);
  const intervalRef = useRef(null);

  const hasBoth = Boolean(frame1) && Boolean(frame2);

  useEffect(() => {
    if (!hasBoth) {
      setReady(false);
      return undefined;
    }

    let cancelled = false;
    const preload = (src) => {
      const img = new Image();
      img.src = src;
      // decode() no existe en navegadores muy viejos; el catch cubre errores de red.
      return img.decode ? img.decode().catch(() => {}) : Promise.resolve();
    };

    Promise.all([preload(frame1), preload(frame2)]).then(() => {
      if (!cancelled) setReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [hasBoth, frame1, frame2]);

  useEffect(() => {
    if (!hasBoth || !playing || !ready) {
      setShowSecond(false);
      return undefined;
    }

    intervalRef.current = setInterval(() => {
      setShowSecond((prev) => !prev);
    }, CYCLE_MS);

    return () => clearInterval(intervalRef.current);
  }, [hasBoth, playing, ready]);

  const single = frame1 || frame2;

  if (!single) {
    return (
      <Box
        sx={{
          height,
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor,
          color: 'text.secondary',
          borderRadius: radius
        }}>
        <FitnessCenter fontSize="large" />
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', height, width: '100%', overflow: 'hidden', borderRadius: radius, bgcolor }}>
      <Box
        component="img"
        src={frame1 || frame2}
        alt={alt}
        sx={{
          position: 'absolute',
          inset: 0,
          height: '100%',
          width: '100%',
          objectFit: 'contain',
          opacity: hasBoth && showSecond ? 0 : 1,
          transition: FADE
        }}
      />
      {hasBoth && (
        <Box
          component="img"
          src={frame2}
          alt={alt}
          sx={{
            position: 'absolute',
            inset: 0,
            height: '100%',
            width: '100%',
            objectFit: 'contain',
            opacity: showSecond ? 1 : 0,
            transition: FADE
          }}
        />
      )}
    </Box>
  );
}

StopMotionImage.propTypes = {
  frame1: PropTypes.string,
  frame2: PropTypes.string,
  alt: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  playing: PropTypes.bool,
  radius: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  bgcolor: PropTypes.string
};

export default StopMotionImage;
